import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { getEssay } from "@/content/insights";
import type { Section } from "@/content/insights/types";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

const renderSection = (section: Section, i: number) => {
  switch (section.type) {
    case "h2":
      return (
        <h2 key={i} className="mt-14 text-2xl font-bold uppercase tracking-tight text-foreground md:text-3xl">
          {section.content}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="mt-10 text-xl font-semibold text-foreground md:text-2xl">
          {section.content}
        </h3>
      );
    case "p":
      return (
        <p key={i} className="mt-6 text-lg leading-relaxed text-body md:text-xl">
          {section.content}
        </p>
      );
    case "quote":
      return (
        <blockquote
          key={i}
          className="my-12 border-l-2 border-primary bg-surface/50 px-6 py-5 text-xl font-medium italic leading-relaxed text-foreground/90 md:text-2xl"
        >
          {section.content}
        </blockquote>
      );
    case "list": {
      const Tag = section.ordered ? "ol" : "ul";
      return (
        <Tag
          key={i}
          className={`mt-6 space-y-3 pl-6 text-lg leading-relaxed text-body md:text-xl ${
            section.ordered ? "list-decimal" : "list-disc"
          }`}
        >
          {section.items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </Tag>
      );
    }
  }
};

const InsightDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const essay = slug ? getEssay(slug) : undefined;

  if (!essay) return <Navigate to="/insights" replace />;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: essay.title,
    description: essay.dek,
    datePublished: essay.publishedAt,
    author: { "@type": "Organization", name: "TruthForge AI" },
    publisher: {
      "@type": "Organization",
      name: "TruthForge AI",
      logo: { "@type": "ImageObject", url: "https://truthforge.ai/favicon.png" },
    },
    image: "https://truthforge.ai/og-image.png",
    mainEntityOfPage: `https://truthforge.ai/insights/${essay.slug}`,
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title={`${essay.title} | TruthForge AI`}
        description={essay.dek}
        path={`/insights/${essay.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <Navigation />

      <main className="px-5 py-20 md:px-8 lg:py-28">
        <article className="mx-auto max-w-3xl">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-body transition hover:text-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All Insights
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-bold uppercase tracking-[0.18em] text-body">
            <span className="text-primary">{essay.tag}</span>
            <span>{formatDate(essay.publishedAt)}</span>
            <span>{essay.readMinutes} min read</span>
          </div>

          <h1 className="mt-6 text-3xl font-black uppercase leading-tight text-foreground md:text-5xl">
            {essay.title}
          </h1>
          <p className="mt-6 text-xl leading-relaxed text-body md:text-2xl">{essay.dek}</p>

          <div className="mt-10 border-t border-border/60 pt-2">
            {essay.sections.map(renderSection)}
          </div>

          <div className="mt-20 grid gap-4 border-t border-border/60 pt-10 sm:grid-cols-2">
            <Link
              to="/whitepaper"
              className="interface-panel group flex items-center justify-between p-6 transition hover:border-primary/45"
            >
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">White Paper</p>
                <p className="mt-2 text-base font-semibold text-foreground">See the Game</p>
              </div>
              <ArrowRight className="h-4 w-4 text-primary transition group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#contact-form"
              className="interface-panel group flex items-center justify-between p-6 transition hover:border-primary/45"
            >
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">Talk to us</p>
                <p className="mt-2 text-base font-semibold text-foreground">Request a stress-test</p>
              </div>
              <ArrowRight className="h-4 w-4 text-primary transition group-hover:translate-x-1" />
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default InsightDetail;
